import axios from "axios";
import API_URL from "../Config";

export const MI_STATUS_OPTIONS = ["Draft", "Submitted", "Reviewed", "Approved"];

export const MI_EVALUATION_TYPE_OPTIONS = [
  "Annual",
  "Half-Yearly",
  "Quarterly",
  "Special",
];

export const MI_DEPOT_CAPACITY_OPTIONS = [
  { value: "gt150000", label: "> 150000" },
  { value: "50000_to_150000", label: "50000 to 150000" },
  { value: "lt50000", label: "< 50000" },
];

export const MI_AFS_SALES_VOLUME_OPTIONS = [
  { value: "gt50000", label: "> 50000" },
  { value: "10000_to_50000", label: "10000 to 50000" },
  { value: "lt10000", label: "< 10000" },
];

export const MI_ZONE_OPTIONS = [
  { value: "A", label: "Zone A" },
  { value: "B", label: "Zone B" },
];

export const MI_DEPOT_OPERATION_OPTIONS = [
  { value: "tanker", label: "Tanker" },
  { value: "tank_wagon", label: "Tank Wagon" },
  { value: "tt", label: "TT" },
  { value: "tt_farm", label: "TT Farm" },
  { value: "pipeline", label: "Pipeline" },
];

export const MI_AFS_CUSTOMER_OPTIONS = [
  { value: "international", label: "International" },
  { value: "civil", label: "Civil" },
  { value: "defence", label: "Defence" },
];

const NEGATIVE_MARK_BANDS = [
  { lowerExclusive: 75, upperInclusive: 100, mark1To2: -0.8, markGt2: -1.6 },
  { lowerExclusive: 50, upperInclusive: 75, mark1To2: -0.6, markGt2: -1.2 },
  { lowerExclusive: 25, upperInclusive: 50, mark1To2: -0.4, markGt2: -0.8 },
  { lowerExclusive: 0, upperInclusive: 25, mark1To2: -0.2, markGt2: -0.4 },
];

const DEPOT_COMPLEXITY_WEIGHTS = {
  capacity: {
    gt150000: 1.0,
    "50000_to_150000": 0.9,
    lt50000: 0.8,
  },
  zone: {
    A: 1.0,
    B: 0.8,
  },
  operations: {
    tanker: 0.1,
    tank_wagon: 0.2,
    tt: 0.3,
    tt_farm: 0.3,
    pipeline: 0.1,
  },
};

const AFS_COMPLEXITY_WEIGHTS = {
  sales_volume: {
    gt50000: 1.0,
    "10000_to_50000": 0.9,
    lt10000: 0.8,
  },
  zone: {
    A: 1.0,
    B: 0.8,
  },
  customers: {
    international: 0.4,
    civil: 0.4,
    defence: 0.2,
  },
};

export const getMiAuthHeaders = () => ({
  Authorization: localStorage.getItem("authKey") || "",
});

export const parseMiApiError = (
  error,
  fallbackMessage = "Something went wrong",
) => {
  if (error?.response?.data?.error) return error.response.data.error;
  if (error?.response?.data?.message) return error.response.data.message;
  if (error?.message) return error.message;
  return fallbackMessage;
};

export const formatMiDate = (value) => {
  if (!value) return "NA";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "NA";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  }).format(date);
};

export const formatMiDateTime = (value) => {
  if (!value) return "NA";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "NA";

  const formatted = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  }).format(date);

  return `${formatted} IST`;
};

export const formatMiNumber = (value, digits = 2) => {
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return "NA";
  return numberValue.toFixed(digits);
};

export const formatMiPercent = (value, digits = 2) => {
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return "NA";
  return `${numberValue.toFixed(digits)}%`;
};

export const fetchMiLocations = async (params = {}) => {
  const response = await axios.get(`${API_URL}/api/mi/locations`, {
    headers: getMiAuthHeaders(),
    params,
  });
  return response.data || {};
};

export const fetchMiEvaluationCategories = async () => {
  const categoryResponse = await axios.get(`${API_URL}/api/mi/categories`, {
    headers: getMiAuthHeaders(),
  });

  const categories = categoryResponse.data?.categories || [];
  const itemResponses = await Promise.all(
    categories.map((category) =>
      axios.get(`${API_URL}/api/mi/categories/${category.category_id}/items`, {
        headers: getMiAuthHeaders(),
      }),
    ),
  );

  const itemsByCategory = {};
  categories.forEach((category, index) => {
    itemsByCategory[category.category_id] =
      itemResponses[index].data?.items || [];
  });

  return { categories, itemsByCategory };
};

export const getMiScoreValue = (item) => {
  if (item?.weighted_score !== null && item?.weighted_score !== undefined) {
    return Number(item.weighted_score);
  }
  if (item?.average_score !== null && item?.average_score !== undefined) {
    return Number(item.average_score);
  }
  if (item?.allotted_mark !== null && item?.allotted_mark !== undefined) {
    return Number(item.allotted_mark);
  }
  return null;
};

export const normalizeSubcategoryScores = (scores) => {
  if (!scores || typeof scores !== "object") return null;
  const normalized = Object.entries(scores).reduce(
    (accumulator, [key, value]) => {
      if (value === "" || value === null || value === undefined)
        return accumulator;
      const numericValue = Number(value);
      accumulator[key] = Number.isNaN(numericValue) ? value : numericValue;
      return accumulator;
    },
    {},
  );
  return Object.keys(normalized).length > 0 ? normalized : null;
};

export const itemHasScoreContent = (score = {}, attachments = []) => {
  const hasMark =
    score?.allotted_mark !== "" &&
    score?.allotted_mark !== null &&
    score?.allotted_mark !== undefined;
  const hasSubcategoryScores =
    normalizeSubcategoryScores(score?.subcategory_scores) !== null;
  const hasObservation = Boolean((score?.observations || "").trim());
  const hasAttachments = Array.isArray(attachments) && attachments.length > 0;
  return hasMark || hasSubcategoryScores || hasObservation || hasAttachments;
};

export const buildMiSingleScorePayload = (
  itemId,
  categoryId,
  score = {},
  attachments = [],
) => {
  const payload = new FormData();
  payload.append("item_id", itemId);
  payload.append("category_id", categoryId);

  if (
    score?.allotted_mark !== "" &&
    score?.allotted_mark !== null &&
    score?.allotted_mark !== undefined
  ) {
    payload.append("allotted_mark", score.allotted_mark);
  }

  const normalizedSubcategories = normalizeSubcategoryScores(
    score?.subcategory_scores,
  );
  if (normalizedSubcategories) {
    payload.append(
      "subcategory_scores",
      JSON.stringify(normalizedSubcategories),
    );
  }

  if ((score?.observations || "").trim()) {
    payload.append("observations", score.observations.trim());
  }

  attachments
    .filter((attachment) => attachment.isNew && attachment.file)
    .forEach((attachment, index) => {
      payload.append(
        `docs[${index}][name]`,
        attachment.name || attachment.file.name,
      );
      payload.append(`docs[${index}][file]`, attachment.file);
    });

  return payload;
};

export const buildMiBatchScorePayload = (items = []) => {
  const payload = new FormData();
  const scores = items.map((item) => ({
    item_id: item.item_id,
    category_id: item.category_id,
    allotted_mark:
      item.score?.allotted_mark !== "" &&
      item.score?.allotted_mark !== null &&
      item.score?.allotted_mark !== undefined
        ? Number(item.score.allotted_mark)
        : null,
    subcategory_scores: normalizeSubcategoryScores(
      item.score?.subcategory_scores,
    ),
    observations: (item.score?.observations || "").trim(),
  }));

  payload.append("scores", JSON.stringify(scores));

  items.forEach((item) => {
    item.attachments
      .filter((attachment) => attachment.isNew && attachment.file)
      .forEach((attachment, index) => {
        payload.append(
          `docs_${item.item_id}[${index}][name]`,
          attachment.name || attachment.file.name,
        );
        payload.append(`docs_${item.item_id}[${index}][file]`, attachment.file);
      });
  });

  return payload;
};

export const createPendingMiAttachment = (file) => ({
  client_id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()
    .toString(36)
    .slice(2, 8)}`,
  name: file.name,
  file,
  isNew: true,
  file_size: `${(file.size / 1024).toFixed(2)} KB`,
});

export const getMiAttachmentName = (attachment) =>
  attachment?.name ||
  attachment?.doc_name ||
  attachment?.original_filename ||
  attachment?.filename ||
  "Attachment";

export const getMiAttachmentSize = (attachment) => {
  if (attachment?.file instanceof File) {
    return `${(attachment.file.size / 1024).toFixed(2)} KB`;
  }
  if (attachment?.file_size !== undefined && attachment?.file_size !== null) {
    const numericSize = Number(attachment.file_size);
    if (!Number.isNaN(numericSize)) {
      return `${numericSize.toFixed(2)} KB`;
    }
    return String(attachment.file_size);
  }
  return "Size unavailable";
};

export const downloadMiAttachment = async (attachmentId, filename) => {
  const response = await axios.get(
    `${API_URL}/api/mi/attachments/${attachmentId}/download`,
    {
      headers: getMiAuthHeaders(),
      responseType: "blob",
    },
  );

  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "attachment";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const openMiAttachment = async (attachmentId) => {
  const response = await axios.get(
    `${API_URL}/api/mi/attachments/${attachmentId}/view`,
    {
      headers: getMiAuthHeaders(),
      responseType: "blob",
    },
  );

  const url = window.URL.createObjectURL(response.data);
  window.open(url, "_blank", "noopener,noreferrer");
  window.setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 60000);
};

export const getNegativeMarkFromPercent = (percent, bucket) => {
  const numericPercent = Number(percent);
  if (Number.isNaN(numericPercent) || numericPercent <= 0) return 0;
  if (numericPercent >= 100) return bucket === "gt2" ? -2.0 : -1.0;

  const band = NEGATIVE_MARK_BANDS.find(
    (item) =>
      numericPercent > item.lowerExclusive &&
      numericPercent <= item.upperInclusive,
  );

  if (!band) return 0;
  return bucket === "gt2" ? band.markGt2 : band.mark1To2;
};

export const calculateNegativeMarkingPreview = (draft) => {
  const total1To2 = Number(draft.total_1to2_years) || 0;
  const complied1To2 = Number(draft.complied_1to2_years) || 0;
  const totalGt2 = Number(draft.total_gt2_years) || 0;
  const compliedGt2 = Number(draft.complied_gt2_years) || 0;

  const nc1To2Count = Math.max(total1To2 - complied1To2, 0);
  const ncGt2Count = Math.max(totalGt2 - compliedGt2, 0);
  const nc1To2Percent = total1To2 > 0 ? (nc1To2Count / total1To2) * 100 : 0;
  const ncGt2Percent = totalGt2 > 0 ? (ncGt2Count / totalGt2) * 100 : 0;
  const mark1To2 = getNegativeMarkFromPercent(nc1To2Percent, "1to2");
  const markGt2 = getNegativeMarkFromPercent(ncGt2Percent, "gt2");
  const totalNegativeMarks = mark1To2 + markGt2;

  return {
    nc1To2Count,
    ncGt2Count,
    nc1To2Percent,
    ncGt2Percent,
    mark1To2,
    markGt2,
    totalNegativeMarks,
    actualNegativeMarksDeducted: Math.max(totalNegativeMarks, -2),
  };
};

export const createNegativeMarkingDraft = (negativeMarking) => ({
  major_recommendations_date: negativeMarking?.major_recommendations_date || "",
  total_1to2_years: negativeMarking?.non_compliance_1to2_years || 0,
  complied_1to2_years: 0,
  total_gt2_years: negativeMarking?.non_compliance_gt2_years || 0,
  complied_gt2_years: 0,
  remarks: negativeMarking?.remarks || "",
});

export const getComplexityStateFromApi = (locationType, complexityFactor) => {
  const detailsRaw = complexityFactor?.calculation_details;
  let details = null;

  if (detailsRaw && typeof detailsRaw === "string") {
    try {
      details = JSON.parse(detailsRaw);
    } catch (error) {
      details = null;
    }
  } else if (detailsRaw && typeof detailsRaw === "object") {
    details = detailsRaw;
  }

  if (locationType === "AFS") {
    return {
      sales_volume: details?.sales_volume || "",
      zone: details?.zone || "",
      age_over_25: Boolean(details?.age_over_25),
      customers: Array.isArray(details?.customers) ? details.customers : [],
      remarks: complexityFactor?.remarks || "",
    };
  }

  return {
    capacity: details?.capacity || "",
    zone: details?.zone || "",
    age_over_25: Boolean(details?.age_over_25),
    operations: Array.isArray(details?.operations) ? details.operations : [],
    remarks: complexityFactor?.remarks || "",
  };
};

export const calculateComplexityPreview = (locationType, draft) => {
  const ageWeight = draft.age_over_25 ? 1.0 : 0.8;

  if (locationType === "AFS") {
    const salesWeight =
      AFS_COMPLEXITY_WEIGHTS.sales_volume[draft.sales_volume] || 0;
    const zoneWeight = AFS_COMPLEXITY_WEIGHTS.zone[draft.zone] || 0;
    const customerWeight = (draft.customers || []).reduce(
      (total, customer) =>
        total + (AFS_COMPLEXITY_WEIGHTS.customers[customer] || 0),
      0,
    );
    const total = Math.min(
      3 * salesWeight * zoneWeight * ageWeight * customerWeight,
      3,
    );

    return {
      W: salesWeight,
      P: zoneWeight,
      A: ageWeight,
      C: customerWeight,
      total,
    };
  }

  const capacityWeight = DEPOT_COMPLEXITY_WEIGHTS.capacity[draft.capacity] || 0;
  const zoneWeight = DEPOT_COMPLEXITY_WEIGHTS.zone[draft.zone] || 0;
  const operationWeight = (draft.operations || []).reduce(
    (total, operation) =>
      total + (DEPOT_COMPLEXITY_WEIGHTS.operations[operation] || 0),
    0,
  );
  const total = Math.min(
    3 * capacityWeight * zoneWeight * ageWeight * operationWeight,
    3,
  );

  return {
    W: capacityWeight,
    P: zoneWeight,
    A: ageWeight,
    C: operationWeight,
    total,
  };
};

export const buildComplexityPayload = (locationType, draft) => {
  if (locationType === "AFS") {
    return {
      sales_volume: draft.sales_volume,
      zone: draft.zone,
      age_over_25: Boolean(draft.age_over_25),
      customers: draft.customers || [],
      remarks: (draft.remarks || "").trim() || null,
    };
  }

  return {
    capacity: draft.capacity,
    zone: draft.zone,
    age_over_25: Boolean(draft.age_over_25),
    operations: draft.operations || [],
    remarks: (draft.remarks || "").trim() || null,
  };
};
